import { useState } from 'react'

import { Predict, Slider, useFrames } from './common'

/**
 * Обман зрения (урок 2): два равных отрезка с «усами» наружу и внутрь кажутся разными.
 * Ученик подгоняет нижний отрезок «на глаз», потом линейка показывает правду —
 * поэтому в науке измеряют, а не доверяют глазам.
 */

const ВЕРХ = 160 // длина верхнего отрезка, px; 1 px = 1 мм на «линейке»
const УС = 22
const X0 = 100

export default function IllusionLab() {
  const [низ, setНиз] = useState(ВЕРХ)
  const [усы, setУсы] = useState(true)
  const [линейка, setЛинейка] = useState(0) // 0 — убрана, 1 — приложена (анимация)
  const [мерим, setМерим] = useState(false)

  useFrames((dt) => {
    setЛинейка((x) => Math.max(0, Math.min(1, x + (мерим ? dt : -dt) * 2)))
  })

  const отрезок = (y: number, длина: number, наружу: boolean) => {
    const x1 = X0
    const x2 = X0 + длина
    const к = наружу ? 1 : -1 // «усы» наружу (>—<) — отрезок кажется длиннее, наконечники (<—>) — короче
    return (
      <g>
        <line x1={x1} y1={y} x2={x2} y2={y} className="ink" strokeWidth={3} />
        {усы && (
          <>
            <line x1={x1} y1={y} x2={x1 - к * УС * 0.7} y2={y - УС * 0.7} className="ink" strokeWidth={3} />
            <line x1={x1} y1={y} x2={x1 - к * УС * 0.7} y2={y + УС * 0.7} className="ink" strokeWidth={3} />
            <line x1={x2} y1={y} x2={x2 + к * УС * 0.7} y2={y - УС * 0.7} className="ink" strokeWidth={3} />
            <line x1={x2} y1={y} x2={x2 + к * УС * 0.7} y2={y + УС * 0.7} className="ink" strokeWidth={3} />
          </>
        )}
      </g>
    )
  }

  const линейкаY = 150 + (1 - линейка) * 120
  const делений: number[] = []
  for (let mm = 0; mm <= 220; mm += 10) делений.push(mm)

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 290" role="img" aria-label="Два отрезка с усами">
        {отрезок(70, ВЕРХ, true)}
        {отрезок(150, низ, false)}
        {линейка > 0.01 && (
          <g opacity={линейка}>
            <rect x={X0 - 6} y={линейкаY + 8} width={232} height={26} rx={3} className="fill-ruler ink" strokeWidth={1} />
            {делений.map((mm) => (
              <g key={mm}>
                <line x1={X0 + mm} y1={линейкаY + 8} x2={X0 + mm} y2={линейкаY + (mm % 50 === 0 ? 22 : 16)} className="ink" />
                {mm % 50 === 0 && <text x={X0 + mm} y={линейкаY + 32} textAnchor="middle" className="lab-tick">{mm / 10}</text>}
              </g>
            ))}
            <text x={X0 + 236} y={линейкаY + 32} className="lab-tick">см</text>
            <line x1={X0 + ВЕРХ} y1={70} x2={X0 + ВЕРХ} y2={линейкаY + 8} className="ink-heavy" strokeDasharray="3 3" />
            <line x1={X0 + низ} y1={150} x2={X0 + низ} y2={линейкаY + 8} className="ink-el" strokeDasharray="3 3" />
          </g>
        )}
      </svg>

      <div className="lab-readout">
        <span>верхний: {мерим ? `${ВЕРХ / 10} см` : '?'}</span>
        <span>нижний: {мерим ? `${(низ / 10).toFixed(1).replace('.', ',')} см` : '?'}</span>
      </div>
      {мерим && (
        <div className="lab-caption">
          {низ === ВЕРХ
            ? 'Отрезки равны, хотя на глаз нижний кажется короче.'
            : `На глаз ты подогнал нижний отрезок, а он ${низ > ВЕРХ ? 'длиннее' : 'короче'} на ${Math.abs(низ - ВЕРХ) / 10} см. Глаза обманули.`}
        </div>
      )}

      <Predict
        question="Какой отрезок длиннее — верхний или нижний?"
        options={['верхний', 'нижний', 'они равны']}
        right={низ === ВЕРХ ? 2 : низ > ВЕРХ ? 1 : 0}
        revealed={мерим}
        resetKey={String(низ)}
        explain="«Усы» обманывают глаз. Поэтому в физике длину измеряют линейкой, а не оценивают на глаз."
      />

      <Slider label="Длина нижнего отрезка — подгони на глаз" value={низ} min={110} max={210} step={2} unit="" shown="" onChange={(v) => { setНиз(v); setМерим(false) }} />
      <div className="row-wrap" style={{ marginTop: 10 }}>
        <button className="btn btn-primary" onClick={() => setМерим(true)}>📏 Измерить линейкой</button>
        <button className={усы ? 'btn' : 'btn btn-primary'} onClick={() => setУсы((x) => !x)}>{усы ? 'Убрать «усы»' : 'Вернуть «усы»'}</button>
        <button className="btn" onClick={() => { setНиз(ВЕРХ); setМерим(false) }}>Сначала</button>
      </div>
      <p className="muted small">
        Наблюдение без измерения может обмануть. Научный метод — проверять догадки опытом и
        измерением.
      </p>
    </div>
  )
}
